import { Button, Chip } from 'components/ui'
import React from 'react'
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'
import { GripVertical, Trash2 } from 'lucide-react'
import keys from 'constants/keys'
import { Content, Subtask } from '../../model/types'
import { subtaskTypeKeys } from '../../model/subtaskTypes'
import { AI_IMAGE_BUTTON_TYPE } from '../../model/subtaskTypes'
import type { useSubtaskMenu } from '../../hooks/useSubtaskMenu'
import { useCreationText } from '../../hooks/useCreationText'
import { SortableSubtaskCard } from './SortableSubtaskCard'
import { PreviewTitle } from './PreviewTitle'
import { PreviewAttachments } from './PreviewAttachments'
import styles from './styles.module.css'

interface SubtaskPreviewCardProps {
  removable?: boolean
  sortable?: boolean
  grouped?: boolean
  field: Subtask | Content
  draft?: Record<string, any>
  index: number
  isAi: boolean
  isSelected: boolean
  hasError: boolean
  busy: boolean
  rtl: boolean
  Icon?: ReturnType<typeof useSubtaskMenu>['buttons'][number]['Component']
  onSelect: () => void
  onRemove: () => void
}

export function SubtaskPreviewCard({
  sortable = true,
  removable = true,
  grouped = false,
  field,
  draft,
  index,
  isAi,
  isSelected,
  hasError,
  busy,
  rtl,
  Icon,
  onSelect,
  onRemove,
}: SubtaskPreviewCardProps) {
  const t = useCreationText()

  const value = draft || field
  const type = isAi ? AI_IMAGE_BUTTON_TYPE : field.type
  const key = subtaskTypeKeys[type]
  const status = hasError ? 'error' : isSelected ? 'editing' : 'setup'
  const content = field.type === keys.SUBTASK_TYPES.DESCRIPTION ? value : value.content
  const images: string[] = content?.images || []
  const videos: string[] = content?.videos || []
  const files: string[] = content?.files || []
  const hasAttachments = images.length + videos.length + files.length > 0

  const renderContent = (handleProps?: DraggableProvidedDragHandleProps) => (
    <>
      <div className={`${styles.cardActions} ${grouped && sortable ? styles.groupedActions : ''}`}>
        {sortable && (
          <Button
            variant="icon"
            type="button"
            {...handleProps}
            className={styles.dragHandle}
            width="32px"
            minWidth="32px"
            height="32px"
            padding="0"
            color="#74808b"
            borderRadius="6px"
            title={t.reorder}
            aria-label={t.reorder}
            isDisabled={busy}
          >
            <GripVertical size={18} />
          </Button>
        )}
        {removable && (
          <Button
            variant="icon"
            type="button"
            width="32px"
            minWidth="32px"
            height="32px"
            padding="0"
            color="#c74740"
            borderRadius="6px"
            _hover={{ color: '#a82d28', bg: '#fdebea' }}
            title={t.remove}
            aria-label={t.remove}
            isDisabled={busy}
            onClick={onRemove}
          >
            <Trash2 size={16} />
          </Button>
        )}
      </div>
      <button
        type="button"
        className={`${styles.cardSelect} ${grouped && sortable ? styles.groupedSelect : ''}`}
        aria-pressed={isSelected}
        data-subtask-select
        disabled={busy}
        onClick={onSelect}
      >
        {Icon && (
          <div className={styles.cardIcon}>
            <Icon width="54px" height="54px" iconWidth={30} iconHeight={30} />
          </div>
        )}
        <div className={styles.cardCopy}>
          <div className={styles.cardMeta}>
            {field.type !== keys.SUBTASK_TYPES.DESCRIPTION && <span className={styles.typeLabel}>{t[key]}</span>}
            {status === 'error' && (
              <Chip
                label={<>{status === 'error' ? t.needsAttention : t[status]}</>}
                size="sm"
                radius="5px"
                color="#be4742"
                bg="#fdeaea"
                border="none"
              />
            )}
            {field.type !== keys.SUBTASK_TYPES.DESCRIPTION && value.is_required !== false && (
              <Chip
                label={<>{t.required}</>}
                style={{ flexShrink: 0 }}
                size="sm"
                radius="5px"
                color="#8a5415"
                bg="#fff0db"
                border="none"
              />
            )}
            {value.category && !grouped && (
              <Chip
                label={<span dir="auto">{value.category}</span>}
                title={`${t.category}: ${value.category}`}
                className={styles.cardCategory}
                size="sm"
                radius="5px"
                color="#74808b"
                bg="#f1f3f5"
                border="none"
              />
            )}
            {field.type === keys.SUBTASK_TYPES.OPEN_QUESTION && value.is_numeric && (
              <Chip label={<>{t.numericLabel}</>} size="sm" radius="5px" color="#62517e" bg="#eeebf7" border="none" />
            )}
          </div>
          {field.type === keys.SUBTASK_TYPES.DESCRIPTION ? (
            <strong>{t.description}</strong>
          ) : (
            <PreviewTitle value={value.title} fallback={t.untitled} />
          )}
          <div className={styles.cardDetails}>
            {hasAttachments && <PreviewAttachments images={images} videos={videos} files={files} />}
            {value.external_link && <p>{value.external_link}</p>}
            {value.options?.length > 0 && (
              <p>
                {value.options.length} {t.options} · {value.is_multi_choice ? t.multiple : t.single}
              </p>
            )}
          </div>
        </div>
      </button>
    </>
  )
  return sortable ? (
    <SortableSubtaskCard id={field.id!} index={index} disabled={busy} status={status} rtl={rtl}>
      {renderContent}
    </SortableSubtaskCard>
  ) : (
    <article className={`${styles.card} ${styles[status]}`} data-subtask-state={status}>
      {renderContent()}
    </article>
  )
}
